<template>
  <div class="index-list-over-net">
    <button
      v-for="indexNode in foregroundIndexNodes"
      class="index-list-over-net__item"
      :class="{ '-selected': selection.selected.has(indexNode.uid) }"
      @click="select(indexNode)"
      @contextmenu="onContextMenu($event, indexNode)"
    >
      <template v-if="indices[indexNode.uid]">
        <span>{{ indices[indexNode.uid].title }}{{ $t(":") }}</span>
        <strong
          >{{ formatNumber(elementValues[indices[indexNode.uid].uid]?.value, {
            maximumFractionDigits: 2
          }) }}&hairsp;{{
            elementValues[indices[indexNode.uid].uid]?.unit
          }}</strong
        >
      </template>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { formatNumber } from "@/helpers/utility/Formatting";
import { useElements } from "../element/elements";

import type { ExpressionValue } from "@editor/items/expression/expressionFunctions";
import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import type { SceneTreeNode } from "../scene/SceneTreeItem";
import type { Uid } from "@/helpers/utility/Id";
import type { IndexItem } from "./IndexItem";

const props = defineProps<{
  foregroundIndexNodes: SceneTreeNode<"element">[];
  selection: CanvasSelection;
  elementValues: Record<string, ExpressionValue>;
}>();

const emit = defineEmits<{
  (e: "select", item: SceneTreeNode<"element">): void;
  (e: "contextmenu", event: MouseEvent & { items?: { uid: Uid<"sceneNode"> }[] }): void;
}>();

const elements = useElements();

const indices = computed(() => {
  return props.foregroundIndexNodes.reduce((dict, indexTreeNode) => {
    const sceneNode = indexTreeNode.node;
    const elementUid = sceneNode.data.elementUid;
    const element = elements.byUid[elementUid];
    if (element && element.type === "index") {
      dict[sceneNode.uid] = element;
    }
    return dict;
  }, {} as Record<Uid<"sceneNode">, IndexItem>);
});

const select = (indexNode: SceneTreeNode<"element">) => {
  emit("select", indexNode);
};

const onContextMenu = (event: MouseEvent & { items?: { uid: Uid<"sceneNode"> }[] }, indexNode: SceneTreeNode<"element">) => {
  event.preventDefault();
  const sceneNode = indexNode.node;
  event.items = [{ uid: sceneNode.uid }];
  emit("contextmenu", event);
}
</script>

<style lang="scss">
.index-list-over-net {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 100%;
  overflow: auto;

  &__item {
    all: unset;
    padding: 0.5em 1em;
    background-color: var(--color-background-medium);
    border: none;
    text-align: right;
    color: var(--color-primary);
    max-width: 15rem;
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 0.25rem;

    strong {
      white-space: nowrap;
    }

    &:focus-visible {
      outline: 1px solid var(--color-primary);
      outline-offset: -1px;
    }

    &.-selected {
      outline: 2px solid var(--color-primary);
      outline-offset: -2px;
    }
  }
}
</style>